import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  TextField,
  useTheme,
} from "@material-ui/core";
import { CropSquare } from "@material-ui/icons";
import { Autocomplete } from "@material-ui/lab";
import { Formik } from "formik";
import React from "react";
import { useSelector } from "react-redux";
import * as yup from "yup";
import { getVectorFields } from "../helper/collection.helper";
import { CollectionService } from "../services";
import { selectSelectedCollection } from "../store/collection/collection.selector";

interface IModalProps {
  open: boolean;
  onClose: () => void;
}

export interface IAdvanceClusterForm {
  vectorField: string;
  alias: string;
  page: number;
  pageSize: number;
  asc: boolean;
}

const validationSchema = yup.object().shape<IAdvanceClusterForm>({
  vectorField: yup.string().required("Vector field is required"),
  alias: yup.string().required("Alias is required"),
  asc: yup.boolean(),
  page: yup.number().required("Page number is required"),
  pageSize: yup.number().required("Page size is required"),
});

const initialForm: IAdvanceClusterForm = {
  vectorField: "",
  alias: "default",
  page: 1,
  pageSize: 20,
  asc: true,
};

const AdvanceClusterAggregationModal: React.FC<IModalProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const selectedCollection = useSelector(selectSelectedCollection);
  const vectorFields = getVectorFields(selectedCollection.data?.schema || {});
  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle
        style={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.text.secondary,
        }}
      >
        Advance cluster aggregation
      </DialogTitle>
      <Formik
        validationSchema={validationSchema}
        initialValues={{ ...initialForm }}
        onSubmit={async (values: IAdvanceClusterForm) => {
          CollectionService.getAdvanceClusterAggregation(values);
          onClose();
        }}
      >
        {({
          handleSubmit,
          values,
          handleChange,
          handleBlur,
          setValues,
          touched,
          errors,
        }) => (
          <form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <DialogContent>
              <Autocomplete
                options={vectorFields}
                value={values.vectorField}
                style={{ marginBottom: theme.spacing(2) }}
                fullWidth
                onChange={(_, value) =>
                  setValues({ ...values, vectorField: value || "" })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Vector field"
                    color="primary"
                    variant="outlined"
                  />
                )}
              />
              <TextField
                autoFocus
                fullWidth
                style={{ marginBottom: theme.spacing(2) }}
                variant="outlined"
                placeholder="Alias is used to name a cluster"
                name="alias"
                value={values.alias}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={touched.alias && errors.alias}
                error={touched.alias && Boolean(errors.alias)}
              />
              <TextField
                fullWidth
                type="number"
                style={{ marginBottom: theme.spacing(2) }}
                variant="outlined"
                placeholder="Page number"
                name="page"
                value={values.page}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={touched.page && errors.page}
                error={touched.page && Boolean(errors.page)}
              />
              <TextField
                fullWidth
                style={{ marginBottom: theme.spacing(2) }}
                type="number"
                variant="outlined"
                placeholder="Page size"
                name="pageSize"
                value={values.pageSize}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={touched.pageSize && errors.pageSize}
                error={touched.pageSize && Boolean(errors.pageSize)}
              />
              <FormControlLabel
                label="Whether to sort results by ascending or descending order"
                labelPlacement="end"
                control={
                  <Checkbox
                    icon={<CropSquare color="primary" />}
                    checked={values.asc}
                    onChange={handleChange}
                    name="asc"
                    color="primary"
                    disableRipple
                  />
                }
              />
            </DialogContent>

            <Divider />
            <DialogActions>
              <Button
                style={{
                  textTransform: "none",
                  borderColor: theme.palette.error.light,
                  color: theme.palette.error.light,
                }}
                variant="outlined"
                disableRipple
                onClick={onClose}
              >
                Close
              </Button>
              <Button
                color="primary"
                type="submit"
                style={{ textTransform: "none" }}
                variant="outlined"
                disableRipple
              >
                Get aggregated data
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AdvanceClusterAggregationModal;
